/**
 * SchwarzschildMath — the CPU half of the black-hole lens (shader gaps K, 2026-09-30): the oracle, the precomputed
 * beam-tracing tables and the CPU mirror of the shader's TraceRay, in units where the Schwarzschild radius r_s = 1
 * (u ≜ r_s / r, the photon sphere at u = 2/3, the ISCO at u = 1/3).
 *
 * After E. Bruneton, "Real-time High-Quality Rendering of Non-Rotating Black Holes" (2020, arXiv 2010.08735): a light
 * ray is reduced to its plane (the camera's radial axis and the view ray), where u(φ) obeys u'' = 3u²/2 − u with
 * e ≜ 1/b (b the impact parameter) and u'² = e² − u²(1 − u). Two small tables replace the per-pixel integration:
 *   D(e², u) — the deflection Δ = φ − atan2(u, u') of a ray arriving from infinity when it reaches u (Appendix A's
 *              mapping piles texels where Δ diverges: e² → μ = 4/27, the photon sphere);
 *   U(e², φ) — the inverse radius u a ray from infinity has reached at polar angle φ (for the disc crossings).
 * Built here by quadrature / RK4 (not Bruneton's Euler march — 512² columns in JavaScript would take minutes).
 *
 * The ORACLE is independent of the tables: `bhPhiOfU` integrates the orbit with a singularity-free substitution at
 * many panels, and `bhTraceOracle` marches the geodesic ODE from the camera with small RK4 steps.
 *
 * ── Licence of the ported parts ──────────────────────────────────────────────────────────────────────────────────
 * The texture-coordinate mappings (`bhDeflectionTexU/V`, `bhInverseRadiusTexU`, `bhPhiUb`, `bhApsisU`), the
 * `bhTraceRayCpu` procedure and the disc Doppler factor are a port of the MATH of Eric Bruneton's black_hole_shader
 * (https://github.com/ebruneton/black_hole_shader, black_hole/functions.glsl and model.glsl) into TypeScript and TSL;
 * no GLSL source is used. Its licence, reproduced as required:
 *
 *   Copyright (c) 2020 Eric Bruneton
 *   All rights reserved.
 *
 *   Redistribution and use in source and binary forms, with or without modification, are permitted provided that
 *   the following conditions are met:
 *   1. Redistributions of source code must retain the above copyright notice, this list of conditions and the
 *      following disclaimer.
 *   2. Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the
 *      following disclaimer in the documentation and/or other materials provided with the distribution.
 *   3. Neither the name of the copyright holder nor the names of its contributors may be used to endorse or promote
 *      products derived from this software without specific prior written permission.
 *
 *   THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED
 *   WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR
 *   PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 *   INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF
 *   SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY
 *   THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING
 *   IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 *
 * Pure: no three, no DOM — vitest pins every number here (tests/unit/black-hole-lensing.test.ts).
 */

// ── constants (r_s = 1) ─────────────────────────────────────────────────────────────────────────────────────────
/** μ = max of u²(1 − u) over [0, 1], at the photon sphere u = 2/3. A ray with e² > μ falls in. */
export const BH_MU = 4 / 27;
/** The photon sphere radius (r_s units): r = 3M = 1.5 r_s. */
export const BH_PHOTON_SPHERE_R = 1.5;
export const BH_PHOTON_SPHERE_U = 2 / 3;
/** The critical impact parameter — the shadow's edge seen from infinity: b_c = 3√3/2 r_s = 1/√μ ≈ 2.598. */
export const BH_CRITICAL_B = Math.sqrt(27) / 2;
/** The innermost stable circular orbit (r_s units): 6M = 3 r_s. */
export const BH_ISCO_R = 3;
/** The radius of the disc's hottest ring for T⁴ ∝ u³(1 − √(3u)) (Bruneton / Lasota 2016): r = 49/12 r_s. */
export const BH_DISC_PEAK_R = 49 / 12;

const clamp = (x: number, lo: number, hi: number): number => (x < lo ? lo : x > hi ? hi : x);

/** The radial "potential" of the orbit equation: u'² = P(u) = e² − u²(1 − u). */
export function bhRadialPotential(e2: number, u: number): number { return e2 - u * u * (1 - u); }

/** The apsis (turning point) of a ray from infinity with e² < μ (Bruneton Eq. 9). */
export function bhApsisU(e2: number): number {
  const x = clamp((2 / BH_MU) * e2 - 1, -1, 1);
  return 1 / 3 + (2 / 3) * Math.sin(Math.asin(x) / 3);
}

// ── Bruneton's texture-coordinate mappings (Appendix A) ─────────────────────────────────────────────────────────
/** D table column: e² ∈ (0, μ) → [0, ½), e² ∈ (μ, ∞) → (½, 1]; both ends of the fold (e² = 0 and ∞) deflect nothing. */
export function bhDeflectionTexU(e2: number): number {
  if (e2 < BH_MU) return 0.5 - Math.sqrt(-Math.log(1 - e2 / BH_MU) / 50);
  return 0.5 + Math.sqrt(-Math.log(1 - BH_MU / e2) / 50);
}
/** The inverse of `bhDeflectionTexU` (the column → its e²). */
export function bhE2FromDeflectionTexU(tu: number): number {
  const x = 1 - Math.exp(-50 * (tu - 0.5) * (tu - 0.5));
  return tu < 0.5 ? BH_MU * x : BH_MU / x;
}
const SQ23 = Math.sqrt(2 / 3), SQ13 = Math.sqrt(1 / 3);
/** D table row: u → [0, 1]; bounded rays end at their apsis (row 1), falling rays at the horizon u = 1. */
export function bhDeflectionTexV(e2: number, u: number): number {
  if (e2 > BH_MU) {
    const x = u < 2 / 3 ? -Math.sqrt(2 / 3 - u) : Math.sqrt(u - 2 / 3);
    return (SQ23 + x) / (SQ23 + SQ13);
  }
  return 1 - Math.sqrt(Math.max(1 - u / bhApsisU(e2), 0));
}
/** The inverse of `bhDeflectionTexV` (the row → its u). */
export function bhUFromDeflectionTexV(e2: number, tv: number): number {
  if (e2 > BH_MU) {
    const x = tv * (SQ23 + SQ13) - SQ23;
    return x < 0 ? 2 / 3 - x * x : 2 / 3 + x * x;
  }
  const s = 1 - tv;
  return bhApsisU(e2) * (1 - s * s);
}
/** U table column: e² → 1 / (1 + 6e²). */
export function bhInverseRadiusTexU(e2: number): number { return 1 / (1 + 6 * e2); }
export function bhE2FromInverseRadiusTexU(tu: number): number { return (1 / tu - 1) / 6; }
/** The U table's φ range: an upper bound of min(φ_apsis, φ_{u = 1/3}) (Bruneton's φ_ub). */
export function bhPhiUb(e2: number): number { return (1 + e2) / (1 / 3 + 2 * e2 * Math.sqrt(e2)); }

// ── the oracle: φ(u) along the ingoing half of a ray from infinity ─────────────────────────────────────────────
// Gauss–Legendre 8-point nodes / weights on [−1, 1].
const GL_X = [-0.9602898564975363, -0.7966664774136267, -0.5255324099163290, -0.1834346424956498, 0.1834346424956498, 0.5255324099163290, 0.7966664774136267, 0.9602898564975363];
const GL_W = [0.1012285362903763, 0.2223810344533745, 0.3137066458778873, 0.3626837833783620, 0.3626837833783620, 0.3137066458778873, 0.2223810344533745, 0.1012285362903763];
function gl8(f: (x: number) => number, a: number, b: number): number {
  const h = 0.5 * (b - a), c = 0.5 * (a + b);
  let s = 0;
  for (let k = 0; k < 8; k++) s += GL_W[k] * f(c + h * GL_X[k]);
  return s * h;
}
/**
 * The substituted integrands. A bounded ray (e² < μ, apsis ua): w = ua(1 − s²) turns φ = ∫dw/√P into
 * ∫ 2√ua / √Qp(w) ds with Qp(w) = (1 − ua)(w + ua) − w² (P = (ua − w)·Qp exactly — no inverse-sqrt at the apsis).
 * A falling ray (e² ≥ μ): w = 2/3 + x|x| stretches the neighbourhood of the photon sphere, dw = 2|x| dx.
 */
function boundedIntegrand(ua: number): (s: number) => number {
  const sq = Math.sqrt(ua);
  return (s: number): number => { const w = ua * (1 - s * s); return (2 * sq) / Math.sqrt(Math.max(1e-300, (1 - ua) * (w + ua) - w * w)); };
}
function fallingIntegrand(e2: number): (x: number) => number {
  return (x: number): number => { const w = 2 / 3 + x * Math.abs(x); return (2 * Math.abs(x)) / Math.sqrt(Math.max(1e-300, bhRadialPotential(e2, w))); };
}
/** φ swept by a ray from infinity until it reaches inverse radius u (ingoing half; u ≤ ua for a bounded ray). */
export function bhPhiOfU(e2: number, u: number, panels = 2000): number {
  if (!(e2 > 0) || u <= 0) return 0;
  if (e2 < BH_MU) {
    const ua = bhApsisU(e2);
    const s0 = Math.sqrt(Math.max(0, 1 - Math.min(u, ua) / ua));
    const f = boundedIntegrand(ua);
    let acc = 0;
    for (let k = 0; k < panels; k++) { const a = s0 + ((1 - s0) * k) / panels, b = s0 + ((1 - s0) * (k + 1)) / panels; acc += gl8(f, a, b); }
    return acc;
  }
  const x0 = -SQ23, x1 = u < 2 / 3 ? -Math.sqrt(2 / 3 - u) : Math.sqrt(Math.min(u, 1) - 2 / 3);
  const f = fallingIntegrand(e2);
  let acc = 0;
  for (let k = 0; k < panels; k++) { const a = x0 + ((x1 - x0) * k) / panels, b = x0 + ((x1 - x0) * (k + 1)) / panels; acc += gl8(f, a, b); }
  return acc;
}
/** The oracle deflection Δ(e², u) = φ(u) − atan2(u, √P(u)) (the D table's definition). */
export function bhDeflectionOracle(e2: number, u: number): number {
  if (!(e2 > 0) || u <= 0) return 0;
  if (e2 < BH_MU) {
    const ua = bhApsisU(e2), w = Math.min(u, ua);
    const s = Math.sqrt(Math.max(0, 1 - w / ua));
    const P = ua * s * s * ((1 - ua) * (w + ua) - w * w); // exact factorisation — no cancellation near the apsis
    return bhPhiOfU(e2, w) - Math.atan2(w, Math.sqrt(Math.max(0, P)));
  }
  return bhPhiOfU(e2, u) - Math.atan2(u, Math.sqrt(Math.max(0, bhRadialPotential(e2, u))));
}
/** The weak-field deflection of a ray passing at impact parameter b (r_s units): 2/b + 15π/(16 b²). */
export function bhWeakFieldDeflection(b: number): number { return 2 / b + (15 * Math.PI) / (16 * b * b); }

// ── a compact source behind the hole: the Einstein ring and the two images (the weak field's closed forms) ─────
/**
 * The weak-field Einstein angle of a source straight behind the hole, seen from inverse radius u (= r_s / D_l):
 * θ_E = √(2·u·D_ls/D_s) — i.e. √(2 r_s D_ls / (D_l D_s)). `dlsOverDs` is 1 for a source at infinity (a sky direction).
 */
export function bhEinsteinAngle(u: number, dlsOverDs = 1): number { return Math.sqrt(Math.max(0, 2 * u * dlsOverDs)); }
/**
 * The two images of a POINT source that stands at angle β from the hole's direction (a point lens, weak field):
 * θ± = ½(√(β² + 4θ_E²) ± β) from the hole — `plus` on the source's own side, `minus` mirrored on the far side — and
 * their magnifications μ± = (y² + 2) / (2y√(y² + 4)) ± ½, y = β/θ_E (μ₋ as an absolute value). Lensing conserves
 * surface brightness, so μ is the ratio of image AREA to source area: μ₊ − μ₋ = 1 and θ₊·θ₋ = θ_E² always.
 * β = 0 is the ring (both radii θ_E, μ unbounded for a point — `bhRingOfDisc` has the finite source).
 */
export function bhWeakImages(beta: number, thetaE: number): { plus: number; minus: number; muPlus: number; muMinus: number } {
  const b = Math.abs(beta), te = Math.max(0, thetaE);
  const root = Math.sqrt(b * b + 4 * te * te);
  const plus = 0.5 * (root + b), minus = 0.5 * (root - b);
  if (!(te > 0)) return { plus: b, minus: 0, muPlus: 1, muMinus: 0 };
  const y = b / te;
  if (!(y > 0)) return { plus, minus, muPlus: Infinity, muMinus: Infinity };
  const a = (y * y + 2) / (2 * y * Math.sqrt(y * y + 4));
  return { plus, minus, muPlus: a + 0.5, muMinus: a - 0.5 };
}
/**
 * A uniform DISC of angular radius `a` centred straight behind the hole (weak field): its image is the ring between
 * ½(√(a² + 4θ_E²) ∓ a) — exactly `a` wide — of area π·a·√(a² + 4θ_E²), so the whole source is magnified
 * √(1 + 4θ_E²/a²) times (the finite-source ceiling a point source's μ never reaches).
 */
export function bhRingOfDisc(a: number, thetaE: number): { inner: number; outer: number; magnification: number } {
  const root = Math.sqrt(a * a + 4 * thetaE * thetaE);
  return { inner: 0.5 * (root - a), outer: 0.5 * (root + a), magnification: a > 0 ? root / a : Infinity };
}

// ── the tables ──────────────────────────────────────────────────────────────────────────────────────────────────
export interface BlackHoleTableData {
  /** D(e², u): `dW` × `dH` single-channel, row-major (row j = the texture's v). */
  deflection: Float32Array; dW: number; dH: number;
  /** U(e², φ): `uW` × `uH` single-channel, row-major. */
  inverseRadius: Float32Array; uW: number; uH: number;
}
/**
 * Table sizes by quality: Bruneton ships 512² / 64×32; the low tier halves the deflection table.
 * THE FOLD COLUMN: the D table's WIDTH is odd so a column sits exactly at tu = ½, where both no-deflection limits
 * (e² → 0 and e² → ∞) meet with Δ = 0 at every row. With an even width the bilinear fetch mixed the last bounded
 * column (b ≈ 375 r_s) with the first falling one (a radial ray, whose rows mean other radii): a star 69° off a hole
 * 1000 r_s away was bent 2.7 mrad instead of the 1.46 mrad the oracle and weak-field theory give.
 */
export const BH_TABLE_SIZES = { low: { dW: 257, dH: 256, uW: 64, uH: 32 }, high: { dW: 513, dH: 512, uW: 128, uH: 64 } } as const;

/** D(e², u) by composite Gauss–Legendre between consecutive rows (the rows are the natural panels of the mapping). */
export function bhBuildDeflectionTable(W: number, H: number, sub = 2): Float32Array {
  const out = new Float32Array(W * H);
  for (let i = 0; i < W; i++) {
    const e2 = bhE2FromDeflectionTexU(i / (W - 1));
    if (!(e2 > 0) || !Number.isFinite(e2) || e2 > 1e12) continue; // the fold at tu = ½: a radial ray, no deflection
    if (e2 < BH_MU) {
      const ua = bhApsisU(e2), f = boundedIntegrand(ua);
      let phi = 0, sPrev = 1;
      for (let j = 0; j < H; j++) {
        const s = 1 - j / (H - 1);
        for (let k = 0; k < sub; k++) { const a = sPrev + ((s - sPrev) * k) / sub, b = sPrev + ((s - sPrev) * (k + 1)) / sub; phi += gl8(f, b, a); }
        sPrev = s;
        const u = ua * (1 - s * s);
        const P = ua * s * s * ((1 - ua) * (u + ua) - u * u);
        out[j * W + i] = phi - Math.atan2(u, Math.sqrt(Math.max(0, P)));
      }
    } else {
      const f = fallingIntegrand(e2);
      let phi = 0, xPrev = -SQ23;
      for (let j = 0; j < H; j++) {
        const x = (j / (H - 1)) * (SQ23 + SQ13) - SQ23;
        for (let k = 0; k < sub; k++) { const a = xPrev + ((x - xPrev) * k) / sub, b = xPrev + ((x - xPrev) * (k + 1)) / sub; phi += gl8(f, a, b); }
        xPrev = x;
        const u = 2 / 3 + x * Math.abs(x);
        out[j * W + i] = phi - Math.atan2(u, Math.sqrt(Math.max(0, bhRadialPotential(e2, u))));
      }
    }
  }
  return out;
}

/** One RK4 step of (u, u') under u'' = 3u²/2 − u. */
function rk4(u: number, v: number, h: number): [number, number] {
  const a = (x: number): number => 1.5 * x * x - x;
  const k1u = v, k1v = a(u);
  const k2u = v + 0.5 * h * k1v, k2v = a(u + 0.5 * h * k1u);
  const k3u = v + 0.5 * h * k2v, k3v = a(u + 0.5 * h * k2u);
  const k4u = v + h * k3v, k4v = a(u + h * k3u);
  return [u + (h / 6) * (k1u + 2 * k2u + 2 * k3u + k4u), v + (h / 6) * (k1v + 2 * k2v + 2 * k3v + k4v)];
}
/** U(e², φ) by RK4 from infinity (u = 0, u' = e), `sub` steps per row; the march passes the apsis symmetrically. */
export function bhBuildInverseRadiusTable(W: number, H: number, sub = 48): Float32Array {
  const out = new Float32Array(W * H);
  for (let i = 0; i < W; i++) {
    const e2 = bhE2FromInverseRadiusTexU(clamp(i / (W - 1), 0.001, 0.999));
    const phiUb = bhPhiUb(e2);
    let u = 0, v = Math.sqrt(e2);
    out[i] = 0;
    const h = phiUb / (H - 1) / sub;
    for (let j = 1; j < H; j++) {
      for (let k = 0; k < sub; k++) {
        if (u >= 1) break;
        [u, v] = rk4(u, v, h);
      }
      out[j * W + i] = Math.min(u, 1);
    }
  }
  return out;
}
/** Build both tables at a quality's sizes (the Bruneton layout). ~tens of ms at 'high' in V8. */
export function bhBuildTables(quality: keyof typeof BH_TABLE_SIZES = 'high'): BlackHoleTableData {
  const s = BH_TABLE_SIZES[quality];
  return { deflection: bhBuildDeflectionTable(s.dW, s.dH), dW: s.dW, dH: s.dH, inverseRadius: bhBuildInverseRadiusTable(s.uW, s.uH), uW: s.uW, uH: s.uH };
}

// ── lookups: the CPU mirror of the shader's manual bilinear fetch ───────────────────────────────────────────────
/** Bilinear lookup at unit coordinates (texel CENTRES at 0 and 1 — Bruneton's GetTextureCoordFromUnitRange). */
export function bhBilinear(tab: Float32Array, W: number, H: number, tu: number, tv: number): number {
  const x = clamp(tu, 0, 1) * (W - 1), y = clamp(tv, 0, 1) * (H - 1);
  const i0 = Math.min(W - 2, Math.floor(x)), j0 = Math.min(H - 2, Math.floor(y));
  const fx = x - i0, fy = y - j0;
  const a = tab[j0 * W + i0], b = tab[j0 * W + i0 + 1], c = tab[(j0 + 1) * W + i0], d = tab[(j0 + 1) * W + i0 + 1];
  return (a * (1 - fx) + b * fx) * (1 - fy) + (c * (1 - fx) + d * fx) * fy;
}
/** [Δ(e², u), Δ at the apsis (row 1)] from the D table. */
export function bhLookupDeflection(t: BlackHoleTableData, e2: number, u: number): [number, number] {
  const tu = bhDeflectionTexU(e2);
  return [bhBilinear(t.deflection, t.dW, t.dH, tu, bhDeflectionTexV(e2, u)), bhBilinear(t.deflection, t.dW, t.dH, tu, 1)];
}
export function bhLookupInverseRadius(t: BlackHoleTableData, e2: number, phi: number): number {
  return bhBilinear(t.inverseRadius, t.uW, t.uH, bhInverseRadiusTexU(e2), phi / bhPhiUb(e2));
}

/** GLSL's mod (always in [0, y) for y > 0). */
const gmod = (x: number, y: number): number => x - y * Math.floor(x / y);

export interface BlackHoleTrace {
  /** The escape deflection Δ (the ray leaves along δ' = δ + Δ), or −1: the ray falls into the hole. */
  deflection: number;
  /** The nearer disc crossing: inverse radius (−1 none) and its polar angle in the ray plane. */
  u0: number; phi0: number;
  /** The farther crossing (the second half of a bounded ray). */
  u1: number; phi1: number;
}
/**
 * The CPU mirror of the shader's TraceRay (Bruneton, Algorithm 1): the camera at inverse radius `u`, the view ray at
 * angle `delta` from the camera's OUTWARD radial axis, the disc's line at `alpha` in the ray plane, the disc between
 * inverse radii `uoc` (outer) and `uic` (inner). A crossing outside the disc is reported (the caller's pulse rejects it).
 */
export function bhTraceRayCpu(t: BlackHoleTableData, u: number, delta: number, alpha: number): BlackHoleTrace {
  const out: BlackHoleTrace = { deflection: -1, u0: -1, phi0: 0, u1: -1, phi1: 0 };
  const sinD = Math.sin(delta), cosD = Math.cos(delta);
  const uDot = -u * cosD / Math.max(1e-12, sinD);
  const e2 = uDot * uDot + u * u * (1 - u);
  if (e2 < BH_MU && u > 2 / 3) return out;
  const [def, defA] = bhLookupDeflection(t, e2, u);
  let rayDef = def;
  if (uDot > 0) rayDef = e2 < BH_MU ? 2 * defA - def : -1;
  out.deflection = rayDef;
  const s = uDot > 0 ? 1 : uDot < 0 ? -1 : 0;
  let phi = def + (s === 1 ? Math.PI - delta : delta) + s * alpha;
  const phiApsis = defA + Math.PI / 2;
  const phi0 = gmod(phi, Math.PI);
  const ui0 = bhLookupInverseRadius(t, e2, phi0);
  if (phi0 < phiApsis) {
    const side = s * (ui0 - u);
    if (side > 1e-3 || (side > -1e-3 && alpha < delta)) { out.u0 = ui0; out.phi0 = alpha + phi - phi0; }
  }
  phi = 2 * phiApsis - phi;
  const phi1 = gmod(phi, Math.PI);
  const ui1 = bhLookupInverseRadius(t, e2, phi1);
  if (e2 < BH_MU && s === 1 && phi1 < phiApsis) { out.u1 = ui1; out.phi1 = alpha + phi - phi1; }
  return out;
}

// ── the independent oracle: march the geodesic from the camera ──────────────────────────────────────────────────
/**
 * Trace a ray from a camera at inverse radius `uc` leaving at angle `delta` from the outward radial axis, by RK4 on
 * u'' = 3u²/2 − u in φ (small steps, independent of both tables). Returns the escape angle δ' (from the camera's
 * radial axis, in the ray plane) or null when it falls in, and every crossing of the line at polar angle `alpha`
 * (mod π) as {u, phi}.
 */
export function bhTraceOracle(uc: number, delta: number, alpha = NaN, maxPhi = 60): { escape: number | null; crossings: Array<{ u: number; phi: number }> } {
  let u = uc, v = -uc * Math.cos(delta) / Math.max(1e-12, Math.sin(delta)), phi = 0;
  const crossings: Array<{ u: number; phi: number }> = [];
  let k = 0; // the next crossing of the line is at alpha + kπ (> 0: the camera itself is not a crossing)
  while (phi < maxPhi) {
    // step control: fine near the horizon and wherever u changes fast
    const h = Math.min(2e-4, 2e-4 / Math.max(1, Math.abs(v) * 8));
    const [un, vn] = rk4(u, v, h);
    const pn = phi + h;
    if (Number.isFinite(alpha)) {
      const lineAt = alpha + k * Math.PI;
      if (lineAt > phi && lineAt <= pn) { crossings.push({ u: u + (un - u) * ((lineAt - phi) / h), phi: lineAt }); k++; }
      else if (lineAt <= phi) k++;
    }
    if (un >= 1) return { escape: null, crossings };
    // u reaches 0 = infinity: the ray leaves radially at that polar angle, so δ' = φ∞ (linear in the last step)
    if (un <= 0) return { escape: phi + (h * u) / (u - un), crossings };
    u = un; v = vn; phi = pn;
  }
  return { escape: null, crossings };
}

// ── the observer: the shadow, impact parameters, the core of the picture ───────────────────────────────────────
/**
 * THE STATIC FRAME: a camera hovering at u measures its view angle ψ in its own orthonormal frame, where the radial
 * basis vector is √(1 − u)·∂r (Bruneton Eq. 2). The orbit equation's δ (tan δ = r dφ/dr) is the COORDINATE angle:
 * tan δ = tan ψ / √(1 − u). Skipping the squash shrinks the shadow — at u = 1/30 a ray 1 mrad outside the true edge
 * fell in. `radial` = v·x̂′, `tangential` = v·ŷ′ (≥ 0) of the unit view ray; returns δ ∈ [0, π].
 */
export function bhCoordinateDelta(u: number, radial: number, tangential: number): number {
  return Math.atan2(tangential, radial * Math.sqrt(Math.max(0, 1 - u)));
}
/** The shadow's angular radius seen by a STATIC observer at inverse radius u: sin α = b_c·u·√(1 − u) (past 90° inside the photon sphere). */
export function bhShadowAngularRadius(u: number): number {
  const s = Math.min(1, BH_CRITICAL_B * u * Math.sqrt(Math.max(0, 1 - u)));
  const a = Math.asin(s);
  return u > 2 / 3 ? Math.PI - a : a;
}
/** A static observer's view ray at angle θ from the hole's direction has impact parameter b = sin θ / (u √(1 − u)). */
export function bhImpactParameter(u: number, theta: number): number { return Math.sin(theta) / (u * Math.sqrt(Math.max(1e-12, 1 - u))); }
/** The view angle (from the hole's direction) whose ray has impact parameter b, or π when no ray reaches that far. */
export function bhAngleOfImpact(u: number, b: number): number {
  const s = b * u * Math.sqrt(Math.max(0, 1 - u));
  return s >= 1 ? Math.PI : Math.asin(s);
}

// ── the disc: temperature, Keplerian rotation, Doppler ─────────────────────────────────────────────────────────
/** The thin disc's temperature profile T(r) / T_peak with T⁴ ∝ u³(1 − √(3u)) (zero torque at the ISCO), 0 inside it. */
export function bhDiscTemperatureProfile(r: number): number {
  if (r <= BH_ISCO_R) return 0;
  const prof = (x: number): number => Math.pow((1 - Math.sqrt(3 / x)) / (x * x * x), 0.25);
  return prof(r) / prof(BH_DISC_PEAK_R);
}
/** The Keplerian angular velocity (units c / r_s): Ω = √(M / r³) with M = ½. */
export function bhKeplerOmega(r: number): number { return Math.sqrt(1 / (2 * r * r * r)); }
/**
 * The frequency ratio g = ν_received / ν_emitted for disc gas on a circular geodesic orbit at inverse radius `uHit`
 * seen by a STATIC camera at `uCam` along a ray of constant e² (Bruneton Eq. 11–14):
 *   g = 1 / (√(1 − u_cam) · (A + B·c / e)),   A = √(2 / (2 − 3u)) (= u^t),   B = √(u³ / (2 − 3u)) (= u^t Ω),
 * c = n̂_disc · ẑ′ (ẑ′ = the ray plane's normal x̂′ × d, d the view ray): c < 0 where the gas moves toward the camera.
 * Includes the gravitational redshift at both ends and the transverse Doppler; the intensity scales as g⁴ (bolometric).
 */
export function bhDiscDoppler(uCam: number, uHit: number, e2: number, c: number): number {
  const d = 2 - 3 * uHit;
  const A = Math.sqrt(2 / d), B = Math.sqrt((uHit * uHit * uHit) / d);
  return 1 / (Math.sqrt(Math.max(1e-9, 1 - uCam)) * (A + (B * c) / Math.sqrt(Math.max(1e-12, e2))));
}

// ── the disc over a pixel's footprint (realism pass, 2026-10-07) ────────────────────────────────────────────────────
/** The peak of u³(1 − √(3u)) — the thin disc's T⁴ profile in u = 1/r — at r = 49/12 (normalises it to 1 there). */
export const BH_DISC_PROFILE4_PEAK = (1 - Math.sqrt(3 / BH_DISC_PEAK_R)) / BH_DISC_PEAK_R ** 3;
/** (T / T_peak)⁴ at inverse radius u (0 inside the ISCO). */
export function bhDiscProfile4(u: number): number {
  return u > 0 && u < 1 / BH_ISCO_R ? (u * u * u * (1 - Math.sqrt(3 * u))) / BH_DISC_PROFILE4_PEAK : 0;
}
/** The antiderivative of u³(1 − √(3u)): u⁴/4 − (√3/4.5)·u^4.5. */
export function bhDiscProfile4Integral(u: number): number {
  const u4 = u * u * u * u;
  return u4 / 4 - (Math.sqrt(3) / 4.5) * u4 * Math.sqrt(u);
}
/**
 * The MEAN of (T / T_peak)⁴ over the inverse radii [uLo, uHi] of the disc one pixel covers — in closed form, so a disc
 * a few pixels across carries its flux (a pixel whose centre falls outside the disc used to shade the profile's value
 * THERE, zero, times its coverage: a third of the light of a 12-pixel disc was lost). A sliver returns the point value.
 */
export function bhDiscProfile4Mean(uLo: number, uHi: number): number {
  const lo = clamp(Math.min(uLo, uHi), 0, 1 / BH_ISCO_R), hi = clamp(Math.max(uLo, uHi), 0, 1 / BH_ISCO_R);
  if (hi - lo < 1e-9) return bhDiscProfile4(0.5 * (lo + hi));
  return Math.max(0, (bhDiscProfile4Integral(hi) - bhDiscProfile4Integral(lo)) / (hi - lo)) / BH_DISC_PROFILE4_PEAK;
}

// ── the gas: sheared arcs, band-limited by the pixel's footprint ────────────────────────────────────────────────────
/**
 * The disc's gas texture, as numbers (the TSL in BlackHoleLens reads this table; the measured references are in Atlas's
 * docs/research/2026-10-07-space-realism/references-blackhole.md). Three bands of gradient noise in the co-rotating,
 * pre-wound coordinates (ψ, ln r), ψ = azimuth + winding·ln r − Ω(r)·τ: [cells per radian, cells per unit ln r,
 * amplitude]. Each band is a FACTOR (1 + amplitude·noise)² over its own mean 1 + amplitude²·σ² (`bhGasFactor` — a
 * multiplicative cascade: bright knots on a dimmer ground, positive skew, mean 1 EXACTLY whatever is faded), and each
 * fades to 1 as ONE pixel comes to span a noise cell.
 */
export const BH_GAS = {
  /** d ψ / d ln r of the resting pattern: trailing arcs at a pitch of atan(1 / winding) ≈ 14°. */
  winding: 4,
  /** The extra winding (rad per ln r, at the ISCO) the Keplerian shear may add or remove before a flow phase is replaced. */
  shear: 2.5,
  bands: [[1.3, 2.2, 0.5], [4.5, 10, 0.65], [9, 22, 0.45]] as ReadonlyArray<readonly [number, number, number]>,
  /** The variance of the gradient noise the bands use (MaterialX's 3-D Perlin, × 0.982): what a factor's mean is made of. */
  noiseVar: 0.0703,
  /** Ring lanes: cycles of sin per unit ln r × 2π, and their amplitude. */
  laneFreq: 23, laneAmp: 0.12,
  /**
   * A band's weight is 1 below `fadeLo` noise cells per pixel and 0 above `fadeHi`. Gradient noise's dominant period is
   * ~2 cells, so 0.85 cells a pixel is 2.35 samples a period — gone just before Nyquist; a box filter of the same
   * footprint would still keep 0.85 of the band there (measured), so the fade errs toward a smooth disc, never a crawl.
   */
  fadeLo: 0.3, fadeHi: 0.85,
} as const;
/**
 * The flow cycle (s): how long one phase of the gas lives. Differential rotation winds any advected texture without
 * end, so two phases half a cycle apart are cross-faded and each is replaced before the ISCO's shear exceeds
 * `BH_GAS.shear` either side of the resting winding: T = 2·shear / (1.5·Ω_isco). 6.4 s at the default 12-second orbit.
 */
export function bhDiscFlowCycleS(spinRadPerS: number): number {
  const w = Math.abs(spinRadPerS);
  return clamp((2 * BH_GAS.shear) / (1.5 * Math.max(w, 1e-9)), 2, 60);
}
/** One pixel's extent in a band's noise space (cells): the band's [cells / rad, cells / ln r], the local winding, the pixel's d(azimuth) and d(ln r). */
export function bhGasFootprint(cellsPerRad: number, cellsPerLnR: number, winding: number, dAz: number, dLnR: number): number {
  return Math.hypot(cellsPerRad * (dAz + winding * dLnR), cellsPerLnR * dLnR);
}
/** A band's weight at that footprint: 1 − smoothstep(fadeLo, fadeHi, cells per pixel). */
export function bhGasBandWeight(footprintCells: number): number {
  const t = clamp((footprintCells - BH_GAS.fadeLo) / (BH_GAS.fadeHi - BH_GAS.fadeLo), 0, 1);
  return 1 - t * t * (3 - 2 * t);
}
/**
 * One band's factor: (1 + amp·noise)² over its mean. `left` is the share of the band's variance still drawn (1 unfaded,
 * 0 faded out — then the factor is exactly 1). E[(1 + a·n)²] = 1 + a²·E[n²] for any zero-mean noise: mean 1 by algebra.
 */
export function bhGasFactor(amp: number, noise: number, left = 1): number {
  const f = 1 + amp * noise;
  return (f * f) / (1 + amp * amp * BH_GAS.noiseVar * left);
}
/** Two flow phases of weights w and 1 − w cross-faded WITHOUT losing contrast: (w·a + (1 − w)·b) / √(w² + (1 − w)²). */
export function bhGasCrossfade(w: number, a: number, b: number): number {
  return (w * a + (1 - w) * b) / Math.sqrt(w * w + (1 - w) * (1 - w));
}

// ── the body's seat in depth ────────────────────────────────────────────────────────────────────────────────────────
/**
 * THE BODY'S SEAT. The lens's body is a camera-centred cap; drawn at the hole's own distance it can lie where the depth
 * buffer no longer tells it from the far plane: standard depth is 1 − d ≈ n/z − n/f, and one 24-bit code is 6·10⁻⁸ —
 * at a 0.05 m near plane everything past ~840 km shares the far plane's code. A sky pass drawn after the opaques and
 * depth-tested at the far plane (the engine's BACKGROUND LAST) then paints over the body wherever the rounding falls
 * its way: the sector's "QR code" disc (2026-10-07; a 10 M☉ hole placed 1 477 km out lost 57 % of its disc's pixels).
 * This is the farthest distance at which a surface is still `codes` depth codes nearer than the far plane.
 */
/**
 * The seat's default margin: FOUR depth codes before the far plane (198 km at a 0.05 m near plane). Enough against the
 * sky pass (measured: none of the body's pixels lost; a far planet's shells needed the same four), and — the reason it
 * is not more — clear of a consumer's far-body impostors: Atlas anchors every far planet at 100 km, 8.2 codes before
 * the far plane, so a body seated at 8 codes shared THEIR code and would have fought them wherever they overlap. At 4
 * the order is fixed: the far plane, the hole, the far bodies.
 */
export const BH_BODY_DEPTH_CODES = 4;
export function bhDepthSafeDistance(near: number, far: number, codes = BH_BODY_DEPTH_CODES): number {
  if (!(near > 0) || !(far > near)) return far;
  const c = codes * 2 ** -24;
  return (near * far) / (c * (far - near) + near);
}
/** The standard-depth distance from the far plane, in 24-bit codes, of a surface at `z` (the mirror of the rule above). */
export function bhDepthCodesBeforeFar(near: number, far: number, z: number): number {
  return ((near * (far - z)) / (z * (far - near))) / 2 ** -24;
}

// ── blackbody colour (the engine's LUT convention: chroma with max channel 1, radiance carried separately) ───────
/** The LUT's temperature span (K), log-spaced. */
export const BH_BLACKBODY_T_MIN = 800;
export const BH_BLACKBODY_T_MAX = 40000;
export const BH_BLACKBODY_LUT_SIZE = 128;
const H_ = 6.62607015e-34, C_ = 2.99792458e8, K_ = 1.380649e-23;
/** Planck's spectral radiance (λ in nm, arbitrary but consistent units). */
export function bhPlanck(lambdaNm: number, tK: number): number {
  const l = lambdaNm * 1e-9;
  return (2 * H_ * C_ * C_) / (l ** 5 * Math.expm1((H_ * C_) / (l * K_ * tK)));
}
// CIE 1931 2° colour-matching functions, 380–780 nm every 10 nm (the standard observer's table).
const CMF: ReadonlyArray<readonly [number, number, number]> = [
  [0.001368, 0.000039, 0.00645], [0.004243, 0.00012, 0.02005], [0.01431, 0.000396, 0.06785], [0.04351, 0.00121, 0.2074],
  [0.13438, 0.004, 0.6456], [0.2839, 0.0116, 1.3856], [0.34828, 0.023, 1.74706], [0.3362, 0.038, 1.77211],
  [0.2908, 0.06, 1.6692], [0.19536, 0.09098, 1.28764], [0.09564, 0.13902, 0.81295], [0.03201, 0.20802, 0.46518],
  [0.0049, 0.323, 0.272], [0.0093, 0.503, 0.1582], [0.06327, 0.71, 0.07825], [0.1655, 0.862, 0.04216],
  [0.2904, 0.954, 0.0203], [0.43345, 0.99495, 0.00875], [0.5945, 0.995, 0.0039], [0.7621, 0.952, 0.0021],
  [0.9163, 0.87, 0.00165], [1.0263, 0.757, 0.0011], [1.0622, 0.631, 0.0008], [1.0026, 0.503, 0.00034],
  [0.85445, 0.381, 0.00019], [0.6424, 0.265, 0.00005], [0.4479, 0.175, 0.00002], [0.2835, 0.107, 0],
  [0.1649, 0.061, 0], [0.0874, 0.032, 0], [0.04677, 0.017, 0], [0.0227, 0.00821, 0],
  [0.011359, 0.004102, 0], [0.00579, 0.002091, 0], [0.002899, 0.001047, 0], [0.00144, 0.00052, 0],
  [0.00069, 0.000249, 0], [0.000332, 0.00012, 0], [0.000166, 0.00006, 0], [0.000083, 0.00003, 0],
  [0.000042, 0.000015, 0],
];
function cmf(l: number): readonly [number, number, number] {
  const f = (l - 380) / 10;
  if (f < 0 || f > CMF.length - 1) return [0, 0, 0];
  const i = Math.min(CMF.length - 2, Math.floor(f)), t = f - i, a = CMF[i], b = CMF[i + 1];
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}
/** A blackbody's chroma in linear sRGB (Rec.709 / D65; out-of-gamut lobes clamped; max channel 1). */
export function bhBlackbodyChroma(tK: number): [number, number, number] {
  let X = 0, Y = 0, Z = 0;
  for (let l = 380; l <= 780; l += 2) { const p = bhPlanck(l, tK), [x, y, z] = cmf(l); X += p * x; Y += p * y; Z += p * z; }
  const r = Math.max(0, 3.2404542 * X - 1.5371385 * Y - 0.4985314 * Z);
  const g = Math.max(0, -0.969266 * X + 1.8760108 * Y + 0.041556 * Z);
  const b = Math.max(0, 0.0556434 * X - 0.2040259 * Y + 1.0572252 * Z);
  const m = Math.max(r, g, b, 1e-30);
  return [r / m, g / m, b / m];
}
/** LUT coordinate of a temperature (log axis, texel centres at 0 and 1). */
export function bhBlackbodyLutU(tK: number): number {
  return clamp(Math.log(tK / BH_BLACKBODY_T_MIN) / Math.log(BH_BLACKBODY_T_MAX / BH_BLACKBODY_T_MIN), 0, 1);
}
/** The chroma LUT: `size` RGBA float texels (a = 1), temperature log-spaced over [T_MIN, T_MAX]. */
export function bhBlackbodyLut(size = BH_BLACKBODY_LUT_SIZE): Float32Array {
  const out = new Float32Array(size * 4);
  for (let i = 0; i < size; i++) {
    const t = BH_BLACKBODY_T_MIN * Math.pow(BH_BLACKBODY_T_MAX / BH_BLACKBODY_T_MIN, i / (size - 1));
    const [r, g, b] = bhBlackbodyChroma(t);
    out[i * 4] = r; out[i * 4 + 1] = g; out[i * 4 + 2] = b; out[i * 4 + 3] = 1;
  }
  return out;
}

// ── the whole pixel on the CPU (the bed's pin against the GPU) ─────────────────────────────────────────────────
type V3 = { x: number; y: number; z: number };
const dot = (a: V3, b: V3): number => a.x * b.x + a.y * b.y + a.z * b.z;
const cross = (a: V3, b: V3): V3 => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });
const norm = (a: V3): V3 => { const l = Math.hypot(a.x, a.y, a.z) || 1; return { x: a.x / l, y: a.y / l, z: a.z / l }; };
export interface BlackHolePixelCpu {
  captured: boolean;
  /** The bent (escape) direction, world frame — null when captured. */
  dir: V3 | null;
  deflection: number;
  /** Disc crossings inside [uoc, uic] with their frequency ratio g, nearest first. */
  hits: Array<{ u: number; g: number; pos: V3 }>;
  e2: number;
  /** The ray's impact parameter b = 1/e (r_s units). */
  b: number;
  /**
   * A point of the escape ASYMPTOTE, hole-relative in r_s: the ray leaves along the line `offset + t·dir` (|offset| = b,
   * offset ⟂ dir). A source at a FINITE distance behind the hole is hit when that line passes through it — a direction
   * alone is only right for a source at infinity. Null when captured.
   */
  offset: V3 | null;
}
/**
 * One view ray through the lens, exactly as the shader does it: `ex` the unit vector from the hole to the camera,
 * `u` = r_s / distance, `v` the unit view direction, `n` the disc's spin axis, `uoc`/`uic` its outer/inner inverse
 * radii. Positions are hole-relative in r_s units.
 */
export function bhShadePixelCpu(t: BlackHoleTableData, ex: V3, u: number, v: V3, n: V3, uoc: number, uic: number): BlackHolePixelCpu {
  const zr = cross(ex, v);
  const zl = Math.hypot(zr.x, zr.y, zr.z);
  const ez = zl > 1e-9 ? { x: zr.x / zl, y: zr.y / zl, z: zr.z / zl } : norm(cross(ex, Math.abs(ex.x) < 0.9 ? { x: 1, y: 0, z: 0 } : { x: 0, y: 1, z: 0 }));
  const ey = cross(ez, ex);
  const delta = bhCoordinateDelta(u, dot(v, ex), dot(v, ey));
  let tt = cross(n, ez);
  const tl = Math.hypot(tt.x, tt.y, tt.z);
  tt = tl > 1e-9 ? { x: tt.x / tl, y: tt.y / tl, z: tt.z / tl } : ey;
  if (dot(tt, ey) < 0) tt = { x: -tt.x, y: -tt.y, z: -tt.z };
  const alpha = Math.atan2(dot(tt, ey), dot(tt, ex));
  const tr = bhTraceRayCpu(t, u, delta, alpha);
  const sinD = Math.sin(delta), uDot = -u * Math.cos(delta) / Math.max(1e-12, sinD);
  const e2 = uDot * uDot + u * u * (1 - u);
  const c = dot(n, ez);
  const hits: Array<{ u: number; g: number; pos: V3 }> = [];
  for (const [uh, ph] of [[tr.u0, tr.phi0], [tr.u1, tr.phi1]] as const) {
    if (uh >= uoc && uh <= uic) {
      const p = { x: (ex.x * Math.cos(ph) + ey.x * Math.sin(ph)) / uh, y: (ex.y * Math.cos(ph) + ey.y * Math.sin(ph)) / uh, z: (ex.z * Math.cos(ph) + ey.z * Math.sin(ph)) / uh };
      hits.push({ u: uh, g: bhDiscDoppler(u, uh, e2, c), pos: p });
    }
  }
  const captured = tr.deflection < 0;
  let dir: V3 | null = null, offset: V3 | null = null;
  const b = 1 / Math.sqrt(Math.max(e2, 1e-300));
  if (!captured) {
    const dp = delta + tr.deflection;
    const cs = Math.cos(dp), sn = Math.sin(dp);
    dir = { x: cs * ex.x + sn * ey.x, y: cs * ex.y + sn * ey.y, z: cs * ex.z + sn * ey.z };
    // the angular momentum is conserved (L = b·ẑ′ on the way in and on the way out): offset × dir = b·ẑ′
    offset = { x: b * (sn * ex.x - cs * ey.x), y: b * (sn * ex.y - cs * ey.y), z: b * (sn * ex.z - cs * ey.z) };
  }
  return { captured, dir, deflection: tr.deflection, hits, e2, b, offset };
}

// ── the image of a point behind the hole, through the tables (the CPU oracle of a lensed sun) ──────────────────
/**
 * How far the escape asymptote of the view ray at angle `theta` from the hole's direction (on the source's side for
 * `side` +1, the far side for −1) passes from a source standing at angle `beta` from the hole's direction,
 * `sourceDistRs` from the CAMERA (Infinity = a sky direction): the signed sine of the angle between the ray's escape
 * direction and the direction from the asymptote to the source, in the plane of camera, hole and source. 0 = the ray
 * arrives from the source's centre. NaN when the hole captures the ray or the source is behind the escaping ray.
 */
export function bhSourceMiss(t: BlackHoleTableData, u: number, theta: number, beta: number, sourceDistRs = Infinity, side: 1 | -1 = 1): number {
  const ex: V3 = { x: 1, y: 0, z: 0 };
  const v: V3 = { x: -Math.cos(theta), y: side * Math.sin(theta), z: 0 };
  const px = bhShadePixelCpu(t, ex, u, v, { x: 0, y: 0, z: 1 }, 2, 1); // no disc (u never reaches 2)
  if (px.captured || !px.dir || !px.offset) return NaN;
  const sd: V3 = { x: -Math.cos(beta), y: Math.sin(beta), z: 0 };       // camera → source
  if (!Number.isFinite(sourceDistRs)) return px.dir.x * sd.y - px.dir.y * sd.x;
  const w: V3 = { x: 1 / u + sourceDistRs * sd.x - px.offset.x, y: sourceDistRs * sd.y - px.offset.y, z: 0 };
  const wl = Math.hypot(w.x, w.y);
  if (!(wl > 0) || w.x * px.dir.x + w.y * px.dir.y <= 0) return NaN;
  return (px.dir.x * w.y - px.dir.y * w.x) / wl;
}
/**
 * THE IMAGE OF A POINT BEHIND THE HOLE: the view angle θ (from the hole's direction) at which a source standing at
 * angle `beta` from the hole's direction is SEEN — the outermost ray whose escape asymptote passes through it.
 * `secondary` false = the image on the source's own side (θ ≥ β; at β = 0 the Einstein ring's radius), true = the
 * mirrored image on the far side of the hole. Exact to the tables (no weak-field assumption); null when there is none
 * (a source nearer than the hole has no mirrored image; nothing is seen inside the shadow).
 */
export function bhImageAngle(t: BlackHoleTableData, u: number, beta: number, sourceDistRs = Infinity, secondary = false): number | null {
  const side: 1 | -1 = secondary ? -1 : 1;
  const b0 = Math.abs(beta);
  const lo = bhShadowAngularRadius(u) * 1.0005;
  const dls = Number.isFinite(sourceDistRs) ? Math.max(0, 1 - 1 / Math.max(1e-9, u * sourceDistRs)) : 1;
  const weak = bhWeakImages(b0, bhEinsteinAngle(u, dls));
  const guess = secondary ? weak.minus : weak.plus;
  // the outermost root: start outside it (the weak field's answer with room to spare), walk inward to the first sign change
  let hi = Math.min(Math.PI - 1e-4, Math.max(lo * 1.5, guess * 1.8 + 0.05));
  const want = secondary ? -1 : 1; // the sign of the miss well outside the image
  let fHi = bhSourceMiss(t, u, hi, b0, sourceDistRs, side);
  if (!(fHi * want > 0)) { hi = Math.PI - 1e-4; fHi = bhSourceMiss(t, u, hi, b0, sourceDistRs, side); }
  if (!(fHi * want > 0)) return null;
  const steps = 160;
  let a = hi, fa = fHi;
  for (let k = 1; k <= steps; k++) {
    const th = hi + ((lo - hi) * k) / steps;
    const f = bhSourceMiss(t, u, th, b0, sourceDistRs, side);
    if (!Number.isFinite(f)) return null;                    // the walk reached the shadow (or lost the source) first
    if (f * want <= 0) {
      let x0 = th, x1 = a;                                   // f(x0)·want ≤ 0 < f(x1)·want
      for (let i = 0; i < 48; i++) {
        const m = 0.5 * (x0 + x1), fm = bhSourceMiss(t, u, m, b0, sourceDistRs, side);
        if (!Number.isFinite(fm)) return null;
        if (fm * want <= 0) x0 = m; else x1 = m;
      }
      return 0.5 * (x0 + x1);
    }
    a = th; fa = f;
  }
  void fa;
  return null;
}
